import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import swal from "sweetalert";
import l from "components/util/l";

const UnsavedChangesContext = createContext(null);

const confirmDiscardUnsavedChanges = () =>
  swal({
    title: l("Cambios sin guardar%PA812"),
    text: l("Si sales de esta sección, se perderán los cambios pendientes.%PA813"),
    icon: "warning",
    buttons: {
      cancel: l("Permanecer aquí%PA814"),
      confirm: {
        text: l("Salir sin guardar%PA815"),
        value: true,
        className: "swal-button swal-button--danger",
      },
    },
    dangerMode: true,
  });

const UnsavedChangesProvider = ({ children }) => {

  const parentContext = useContext(UnsavedChangesContext);
  const [hasUnsavedChanges, setHasUnsavedChangesState] = useState(false);
  const hasUnsavedChangesRef = useRef(false);
  const dirtySourcesRef = useRef(new Set());

  const syncHasUnsavedChanges = useCallback(() => {
    const nextHasUnsavedChanges = dirtySourcesRef.current.size > 0;
    hasUnsavedChangesRef.current = nextHasUnsavedChanges;
    setHasUnsavedChangesState(nextHasUnsavedChanges);
  }, []);

  const setSourceUnsavedChanges = useCallback((sourceId, hasChanges) => {
    if (hasChanges)
      dirtySourcesRef.current.add(sourceId);
    else
      dirtySourcesRef.current.delete(sourceId);

    syncHasUnsavedChanges();
  }, [syncHasUnsavedChanges]);

  const resetUnsavedChanges = useCallback(() => {
    dirtySourcesRef.current.clear();
    syncHasUnsavedChanges();
  }, [syncHasUnsavedChanges]);

  const confirmBeforeLeaving = useCallback(async (onConfirm) => {
    if (!hasUnsavedChangesRef.current) {
      if (onConfirm) onConfirm();
      return true;
    }

    const shouldLeave = await confirmDiscardUnsavedChanges();
    if (!shouldLeave)
      return false;

    if (onConfirm) onConfirm();
    return true;
  }, []);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnsavedChangesRef.current)
        return undefined;

      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const value = useMemo(() => ({
    hasUnsavedChanges,
    confirmBeforeLeaving,
    resetUnsavedChanges,
    setSourceUnsavedChanges,
  }), [confirmBeforeLeaving, hasUnsavedChanges, resetUnsavedChanges, setSourceUnsavedChanges]);

  if (parentContext)
    return children;

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );

};

const useUnsavedChanges = () => {
  const context = useContext(UnsavedChangesContext);
  const sourceIdRef = useRef(Symbol("unsaved-changes-source"));
  const setSourceUnsavedChanges = context?.setSourceUnsavedChanges;

  const setHasUnsavedChanges = useCallback((hasChanges) => {
    if (!setSourceUnsavedChanges) return;
    setSourceUnsavedChanges(sourceIdRef.current, hasChanges);
  }, [setSourceUnsavedChanges]);

  useEffect(() => () => {
    if (!setSourceUnsavedChanges) return;
    setSourceUnsavedChanges(sourceIdRef.current, false);
  }, [setSourceUnsavedChanges]);

  if (!context)
    throw new Error("useUnsavedChanges must be used inside UnsavedChangesProvider");

  return {
    hasUnsavedChanges: context.hasUnsavedChanges,
    confirmBeforeLeaving: context.confirmBeforeLeaving,
    resetUnsavedChanges: context.resetUnsavedChanges,
    setHasUnsavedChanges,
  };
};

export {
  UnsavedChangesProvider,
  confirmDiscardUnsavedChanges,
  useUnsavedChanges,
};
